import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/errors";

export interface ResourceState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  /** Recharge la ressource. */
  reload: () => void;
}

/**
 * Chargement d'une ressource REST « one-shot » : loading / error / data, avec
 * annulation à la sortie et rechargement manuel. Volontairement minimal — les
 * ressources temps réel passent par `useLiveCollection`.
 *
 * `deps` : quand elles changent, la ressource est rechargée.
 */
export function useResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: ReadonlyArray<unknown>,
): ResourceState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Garde la dernière version du fetcher sans en refaire une dépendance.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);

    fetcherRef.current(controller.signal).then(
      (value) => {
        if (controller.signal.aborted) return;
        setData(value);
        setLoading(false);
      },
      (err) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err);
        setLoading(false);
      },
    );

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

/** Message court et lisible pour une erreur d'API. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Erreur inattendue.";
}

/**
 * `422 out_of_scope` (contrat v1.13, P2-09) : la course visée appartient à un
 * exploitant `decisionScope: false` (TAE / TAD / Calypso) ou à une navette — pas
 * d'erreur à afficher en rouge, mais un état neutre « information seule ».
 */
export function isOutOfScopeError(error: unknown): boolean {
  return error instanceof ApiError && error.code === "out_of_scope";
}
